// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/** Small pure formatters shared across the UI. Kept side-effect free for tests. */

/** Human-readable byte count: 0 → "0 B", 1536 → "1.5 KB", 5_000_000 → "4.8 MB". */
export function formatBytes(bytes: number, digits = 1): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** i;
  // Bytes never need a decimal.
  const d = i === 0 ? 0 : digits;
  return `${value.toFixed(d)} ${units[i]}`;
}

/** "1280 × 720" from a width/height pair. */
export function formatDimensions(w: number, h: number): string {
  return `${Math.round(w)} × ${Math.round(h)}`;
}

/** Megapixels, sensibly rounded: 1280×720 → "0.9 MP", tiny → "0.02 MP". */
export function megapixels(w: number, h: number): string {
  const mp = (w * h) / 1_000_000;
  const digits = mp >= 10 ? 0 : mp >= 0.1 ? 1 : 2;
  return `${mp.toFixed(digits)} MP`;
}

/** Milliseconds as a compact duration: 850 → "0.9 s", 65_000 → "1 m 5 s". */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0.0 s';
  const totalSec = ms / 1000;
  if (totalSec < 60) return `${totalSec.toFixed(1)} s`;
  const m = Math.floor(totalSec / 60);
  const s = Math.round(totalSec - m * 60);
  return `${m} m ${s} s`;
}

/**
 * Build the download filename for an upscaled image.
 * "cat.jpg" @2× → "cat-2x.png". Handles missing/blank names and dotfiles.
 */
export function outputFilename(originalName: string | undefined, scale: number): string {
  const fallback = 'image';
  let stem = fallback;
  if (originalName && originalName.trim()) {
    const base = originalName.trim().replace(/[\\/]+/g, '_');
    const dot = base.lastIndexOf('.');
    // A leading-dot file ("Screenshot") or no extension → use the whole name.
    stem = dot > 0 ? base.slice(0, dot) : base;
    if (!stem) stem = fallback;
  }
  return `${stem}-${scale}x.png`;
}
